// ============================================
// API TYPES
// ============================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// ============================================
// USER TYPES
// ============================================

export interface User {
  id: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;
  languageCode: string;
  createdAt: string;
  lastActiveAt: string;
  subscriptionTier: string;
  subscriptionExpiresAt: string | null;
}

// ============================================
// DUNGEON WORLD - CHARACTER TYPES
// ============================================

export type CharacterClass = 
  | 'bard' | 'barbarian' | 'fighter' | 'wizard' | 'thief'
  | 'immolator' | 'druid' | 'cleric' | 'paladin' | 'ranger';

export type Race = 'human' | 'elf' | 'dwarf' | 'halfling' | 'salamander';

export type Alignment = 'lawful' | 'good' | 'neutral' | 'chaotic' | 'evil';

export type DamageDie = 'd4' | 'd6' | 'd8' | 'd10' | 'd12';

export interface Bond {
  id: string;
  targetId?: string;
  targetName?: string;
  text: string;
  resolved: boolean;
}

export interface Item {
  id?: string;
  name: string;
  nameRu?: string;
  weight: number;
  quantity?: number;
  tags: string[];
  damage?: string;
  armor?: number;
  uses?: number;
  ammo?: number;
}

export interface Character {
  id: string;
  userId: string;

  name: string | null;
  characterClass: CharacterClass | null;
  race: Race | null;
  alignment: Alignment | null;
  level: number;
  xp: number;

  look: string | null;

  strength: number;
  dexterity: number;
  constitution: number;
  intelligence: number;
  wisdom: number;
  charisma: number;

  maxHp: number;
  currentHp: number;
  armor: number;

  maxLoad: number;
  load: number;

  // Fatigue system
  fatigue: number;
  maxFatigue: number;

  moves: Move[];
  inventory: Item[];

  // Coins (three types)
  copper: number;
  silver: number;
  gold: number;

  bonds: Bond[];

  avatarUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Move {
  key: string;
  name: string;
  description: string;
}

// ============================================
// ROOM TYPES
// ============================================

export interface Room {
  id: string;
  name: string;
  ownerId: string;
  maxPlayers: number;
  isActive: boolean;
  isPublic: boolean;
  inviteCode: string;
  currentScene?: string;
  gameState: object;
  createdAt: string;
  updatedAt: string;
}

export interface RoomMember {
  id: string;
  roomId: string;
  oderId: string;
  odMemberId?: string;
  userId: string;
  role: 'master' | 'player' | 'spectator';
  joinedAt: string;
}

// ============================================
// MESSAGE TYPES
// ============================================

export type MessageType = 'player' | 'master' | 'system' | 'roll' | 'action';

export interface Message {
  id: string;
  roomId: string;
  oderId?: string;
  userId?: string;
  characterId?: string;
  type: MessageType;
  content: string;
  metadata?: {
    move?: string;
    dice?: number[];
    modifier?: number;
    total?: number;
    outcome?: 'miss' | 'partial' | 'success' | 'critical';
  };
  createdAt: string;
}

// ============================================
// AUTH TYPES
// ============================================

export interface AuthResponse {
  token: string;
  user: User;
  character: Character | null;
  isNewUser: boolean;
}

// ============================================
// GUILD TYPES
// ============================================

export type GuildType = 'adventuring_party' | 'guild' | 'faction';
export type GuildRole = 'leader' | 'officer' | 'member';

export interface Guild {
  id: string;
  name: string;
  description: string | null;
  guildType: GuildType;
  leaderId: string | null;
  memberCount: number;
  avatarUrl: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface GuildMember {
  id: string;
  odMemberId: string;
  odMemberGuildId: string;
  role: GuildRole;
  joinedAt: string;
  userName: string;
  userAvatar: string | null;
  characterName: string | null;
  characterClass: CharacterClass | null;
  characterLevel: number;
  xp: number;
}

// ============================================
// LEADERBOARD TYPES
// ============================================

export interface LeaderboardEntry {
  rank: number;
  odLeaderUserId: string;
  userName: string;
  characterName: string | null;
  characterClass: CharacterClass | null;
  characterLevel: number;
  xp: number;
  guildName?: string;
}

// ============================================
// CLASS DATA
// ============================================

export const CLASS_DATA: Record<CharacterClass, {
  nameRu: string;
  baseHP: number;
  damageDie: DamageDie;
  baseLoad: number;
}> = {
  bard:       { nameRu: 'Бард',         baseHP: 6,  damageDie: 'd6',  baseLoad: 9  },
  barbarian:  { nameRu: 'Варвар',       baseHP: 8,  damageDie: 'd10', baseLoad: 8  },
  fighter:    { nameRu: 'Воин',         baseHP: 10, damageDie: 'd10', baseLoad: 12 },
  wizard:     { nameRu: 'Волшебник',    baseHP: 4,  damageDie: 'd4',  baseLoad: 7  },
  thief:      { nameRu: 'Вор',          baseHP: 6,  damageDie: 'd8',  baseLoad: 9  },
  immolator:  { nameRu: 'Испепелитель', baseHP: 4,  damageDie: 'd8',  baseLoad: 9  },
  druid:      { nameRu: 'Друид',        baseHP: 6,  damageDie: 'd6',  baseLoad: 6  },
  cleric:     { nameRu: 'Жрец',         baseHP: 8,  damageDie: 'd6',  baseLoad: 10 },
  paladin:    { nameRu: 'Паладин',      baseHP: 10, damageDie: 'd10', baseLoad: 12 },
  ranger:     { nameRu: 'Следопыт',     baseHP: 8,  damageDie: 'd8',  baseLoad: 11 },
};

export const RACE_NAMES: Record<Race, string> = {
  human: 'Человек',
  elf: 'Эльф',
  dwarf: 'Гном',
  halfling: 'Полурослик',
  salamander: 'Саламандра',
};

export const ALIGNMENT_NAMES: Record<Alignment, string> = {
  lawful: 'Законное',
  good: 'Доброе',
  neutral: 'Нейтральное',
  chaotic: 'Хаотичное',
  evil: 'Злое',
};

// ============================================
// HELPERS
// ============================================

export function getModifier(value: number): number {
  if (value <= 3) return -3;
  if (value <= 5) return -2;
  if (value <= 8) return -1;
  if (value <= 12) return 0;
  if (value <= 15) return 1;
  if (value <= 17) return 2;
  return 3;
}

export function formatModifier(stat: number): string {
  const mod = getModifier(stat);
  return mod >= 0 ? `+${mod}` : `${mod}`;
}

export function evaluateRoll(total: number): 'miss' | 'partial' | 'success' | 'critical' {
  if (total <= 6) return 'miss';
  if (total <= 9) return 'partial';
  if (total <= 11) return 'success';
  return 'critical';
}

export function calculateMaxHP(characterClass: CharacterClass, constitution: number): number {
  return CLASS_DATA[characterClass].baseHP + constitution;
}

export function calculateMaxLoad(characterClass: CharacterClass, strMod: number): number {
  return CLASS_DATA[characterClass].baseLoad + strMod;
}